import {
  Component,
  computed,
  effect,
  forwardRef,
  inject,
  input,
  OnInit,
  output,
  signal,
  untracked,
} from "@angular/core";
import {
  ControlValueAccessor,
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from "@angular/forms";
import { NgSelectModule } from "@ng-select/ng-select";
import { TranslatePipe } from "@ngx-translate/core";
import { debounceTime, distinctUntilChanged, Subject } from "rxjs";

import { AdaaHelper } from "../../../../core/utils";
import { type ParameterCatalog, ValueText } from "../../../models";
import { LanguageService } from "../../../services";

@Component({
  selector: "adaa-form-dropdown",
  imports: [ReactiveFormsModule, NgSelectModule, TranslatePipe],
  templateUrl: "./form-dropdown.html",
  styleUrl: "./form-dropdown.scss",
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => FormDropdownComponent),
    },
    {
      provide: NG_VALIDATORS,
      multi: true,
      useExisting: forwardRef(() => FormDropdownComponent),
    },
  ],
})
export class FormDropdownComponent implements OnInit, ControlValueAccessor {
  readonly languageService = inject(LanguageService);
  readonly AdaaHelper = AdaaHelper;

  readonly searchChannel$ = new Subject<string>();

  readonly #untilDestroy = AdaaHelper.untilDestroyed();
  readonly #searchChannel = this.searchChannel$
    .asObservable()
    .pipe(debounceTime(500), distinctUntilChanged(), this.#untilDestroy());

  private readonly _requiredFromValidator = signal<boolean>(false);
  private readonly _imperativeInvalid = signal<boolean>(false);
  private _disabledByInput = false;
  private _onValidatorChange: () => void;
  private _onTouched: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private _onChange: (value: any) => void;

  readonly required = input<boolean>(false);
  readonly showAsRequired = input<boolean>(false);
  readonly invalid = input<boolean>(false);
  readonly label = input<string>("");
  readonly controlName = input<string | null>(null);
  readonly controlId = input<string | null>(null);
  readonly options = input<ValueText[]>([]);
  readonly searchable = input<boolean>(true);
  readonly clearable = input<boolean>(false);
  readonly classes = input<string>("");
  readonly appendTo = input<string>("");
  readonly enableTooltips = input<boolean>(false);
  readonly inputPlaceholder = input<string>("common.form.label.please_select");
  readonly isDisabled = input<boolean>(false);
  readonly boldLabel = input<boolean>(false);
  readonly setDefaultValue = input<unknown>();
  readonly setValidator = input<{ parameterCatalogs: ParameterCatalog[] }>();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  readonly inputChanges = output<any>();
  readonly onSearch = output<string>();
  readonly onClear = output<null>();

  readonly inputControl = new FormControl();

  readonly effectiveControlId = computed(() => this.controlId() ?? this.controlName());
  readonly isRequired = computed(() => this.required() || this._requiredFromValidator());
  readonly isInvalid = computed(() => this.invalid() || this._imperativeInvalid());

  constructor() {
    effect(() => {
      if (this.isDisabled()) {
        this._disabledByInput = true;
        this.inputControl.disable();
      } else if (this._disabledByInput) {
        this._disabledByInput = false;
        this.inputControl.enable();
      }
    });

    effect(() => {
      const value = this.setDefaultValue();
      if (value === undefined) return;
      untracked(() => {
        if (value !== this.inputControl.value) this.writeValue(value);
      });
    });

    effect(() => {
      const validatorList = this.setValidator();
      const name = this.controlName();
      if (validatorList?.parameterCatalogs) {
        const field = validatorList.parameterCatalogs.find((e) => e.fieldName === name);
        if (field) {
          this._requiredFromValidator.set(field.mandatory === "Y");
        }
      }
    });
  }

  public ngOnInit(): void {
    if (this.searchable()) {
      this.#searchChannel.subscribe({
        next: (term) => this.onSearch.emit(term),
      });
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public writeValue(obj: any): void {
    if (obj === null || obj === undefined) {
      this.inputControl.reset();
      return;
    }
    this.inputControl.setValue(obj);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public registerOnChange(fn: (value: any) => void): void {
    this._onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  public registerOnValidatorChange?(fn: () => void): void {
    this._onValidatorChange = fn;
  }

  public setDisabledState?(isDisabled: boolean): void {
    isDisabled ? this.inputControl.disable() : this.inputControl.enable();
  }

  public validate(_value: FormControl): false | { required: boolean } {
    const required = this.isRequired();
    const isRequired = (required && this.inputControl.value === null) || (required && this.inputControl.value === "");
    return isRequired && { required: true };
  }

  public markInvalid(value: boolean): void {
    this._imperativeInvalid.set(value);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public valueChanged(event: any): void {
    if (this._onChange) this._onChange(event);
    this.inputChanges.emit(event);
  }
}
